import { useTranslation } from 'react-i18next';
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';
import type { ReactNode } from 'react';
import { AppShell } from './components/AppShell.js';
import { PageErrorFallback } from './components/ui/PageErrorFallback.js';
import { TenancyCheckPage } from './pages/TenancyCheckPage.js';
import { DemoExtractionPage } from './pages/DemoExtractionPage.js';
import { ClaimsListPage } from './pages/claims/ClaimsListPage.js';
import { ClaimDetailPage } from './pages/claims/ClaimDetailPage.js';
import { NewClaimPage } from './pages/submit/NewClaimPage.js';
import { ApproverDashboardPage } from './pages/approver/ApproverDashboardPage.js';
import { ApprovalQueuePage } from './pages/approver/ApprovalQueuePage.js';
import { ClaimReviewPage } from './pages/approver/ClaimReviewPage.js';
import { AutoApprovedPage } from './pages/approver/AutoApprovedPage.js';
import { SettledThisWeekPage } from './pages/approver/SettledThisWeekPage.js';
import { ExternalVerificationQueuePage } from './pages/approver/ExternalVerificationQueuePage.js';
import { SettlementQueuePage } from './pages/settlement/SettlementQueuePage.js';
import { SettlementBatchPage } from './pages/settlement/SettlementBatchPage.js';
import { AdminSchemesPage } from './pages/admin/AdminSchemesPage.js';
import { SchemeFormPage } from './pages/admin/SchemeFormPage.js';
import { SchemeUploadPage } from './pages/admin/SchemeUploadPage.js';
import { formatCurrency } from './lib/formatters.js';
import { useDemoMode } from './context/DemoModeContext.js';

function Guarded({ children }: { children: ReactNode }) {
  return <ErrorBoundary FallbackComponent={PageErrorFallback}>{children}</ErrorBoundary>;
}

function HomePage() {
  const { t } = useTranslation();
  const { persona } = useDemoMode();
  const navigate = useNavigate();

  if (persona.role === 'distributor_submitter') {
    return (
      <div className="space-y-6">
        <div>
          <span className="inline-block text-[11px] font-semibold tracking-[0.22em] uppercase text-copper mb-3">
            {t('app.demoSubtitle')}
          </span>
          <h1 className="font-serif italic text-4xl text-ink">
            Infoscend <span className="text-copper">Claims</span>
          </h1>
          <p className="mt-2 text-slate-500 text-sm">Selamat datang, {persona.displayName}.</p>
        </div>
        <button
          type="button"
          onClick={() => navigate('/claims')}
          className="px-5 py-2.5 bg-copper text-bone-soft rounded text-sm font-semibold hover:bg-copper-warm transition-colors"
        >
          {t('claims.title')} →
        </button>
      </div>
    );
  }

  if (persona.role === 'brand_approver' || persona.role === 'brand_admin') {
    navigate('/approver');
    return null;
  }

  return (
    <div className="space-y-8">
      <div>
        <span className="inline-block text-[11px] font-semibold tracking-[0.22em] uppercase text-copper mb-3">
          {t('app.demoSubtitle')}
        </span>
        <h1 className="font-serif italic text-4xl text-ink">
          Infoscend <span className="text-copper">Claims</span>
        </h1>
        <p className="mt-2 text-slate-500 text-sm">
          {t('health.status')}: <strong className="text-success">{t('health.ok')}</strong>
          &nbsp;·&nbsp;
          {t('health.schema')}: <code className="font-mono text-xs">infoscend</code>
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-bone border border-hairline rounded-xl p-6">
          <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-slate-500 mb-1">
            IDR (id locale)
          </div>
          <div className="font-mono text-2xl font-medium text-ink">
            {formatCurrency(1234567, 'IDR', 'id-ID')}
          </div>
        </div>
        <div className="bg-bone border border-hairline rounded-xl p-6">
          <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-slate-500 mb-1">
            IDR (en locale)
          </div>
          <div className="font-mono text-2xl font-medium text-ink">
            {formatCurrency(1234567, 'IDR', 'en-US')}
          </div>
        </div>
        <div className="bg-bone border border-hairline rounded-xl p-6">
          <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-slate-500 mb-1">
            Design tokens
          </div>
          <div className="flex gap-2 mt-1">
            <span className="w-5 h-5 rounded-sm bg-ink" title="ink" />
            <span className="w-5 h-5 rounded-sm bg-copper" title="copper" />
            <span className="w-5 h-5 rounded-sm bg-bone border border-hairline" title="bone" />
            <span className="w-5 h-5 rounded-sm bg-success" title="success" />
            <span className="w-5 h-5 rounded-sm bg-danger" title="danger" />
          </div>
        </div>
      </div>
    </div>
  );
}

function AdminPlaceholderPage() {
  const { t } = useTranslation();
  return (
    <div className="max-w-2xl mx-auto space-y-4">
      <span className="inline-block text-[11px] font-semibold tracking-[0.22em] uppercase text-copper">
        {t('admin.title')}
      </span>
      <h1 className="font-serif italic text-3xl text-ink">{t('admin.title')}</h1>
      <p className="text-slate-500 text-sm">{t('admin.placeholder')}</p>
    </div>
  );
}

function DevFooter() {
  const location = useLocation();
  const { persona } = useDemoMode();
  const navigate = useNavigate();
  const path = location.pathname;

  return (
    <div className="border-t border-hairline py-3 px-6 mt-8">
      <div className="flex gap-4 text-xs font-mono text-slate-400 flex-wrap">
        <button onClick={() => navigate('/')} className={`hover:text-copper transition-colors ${path === '/' ? 'text-copper' : ''}`}>Home</button>
        <button onClick={() => navigate('/dev/tenancy')} className={`hover:text-copper transition-colors ${path.startsWith('/dev/tenancy') ? 'text-copper' : ''}`}>[dev] Tenancy</button>
        <button onClick={() => navigate('/demo/extract')} className={`hover:text-copper transition-colors ${path.startsWith('/demo/extract') ? 'text-copper' : ''}`}>[demo] Extraction</button>
        {persona.role === 'distributor_submitter' && (
          <>
            <button onClick={() => navigate('/claims')} className={`hover:text-copper transition-colors ${path.startsWith('/claims') ? 'text-copper' : ''}`}>Claims List</button>
            <button onClick={() => navigate('/submit/new')} className={`hover:text-copper transition-colors ${path.startsWith('/submit') ? 'text-copper' : ''}`}>New Claim</button>
          </>
        )}
        {(persona.role === 'brand_approver' || persona.role === 'brand_admin') && (
          <>
            <button onClick={() => navigate('/approver')} className={`hover:text-copper transition-colors ${path === '/approver' ? 'text-copper' : ''}`}>Dashboard</button>
            <button onClick={() => navigate('/approver/queue')} className={`hover:text-copper transition-colors ${path.startsWith('/approver/queue') ? 'text-copper' : ''}`}>Queue</button>
            <button onClick={() => navigate('/approver/settlement')} className={`hover:text-copper transition-colors ${path.startsWith('/approver/settlement') ? 'text-copper' : ''}`}>Settlement</button>
          </>
        )}
        {persona.role === 'brand_admin' && (
          <>
            <button onClick={() => navigate('/admin')} className={`hover:text-copper transition-colors ${path === '/admin' ? 'text-copper' : ''}`}>Admin</button>
            <button onClick={() => navigate('/admin/schemes')} className={`hover:text-copper transition-colors ${path.startsWith('/admin/schemes') ? 'text-copper' : ''}`}>Schemes</button>
          </>
        )}
      </div>
    </div>
  );
}

function AppRoutes() {
  return (
    <AppShell footer={<DevFooter />}>
      <Routes>
        <Route path="/" element={<Guarded><HomePage /></Guarded>} />
        <Route path="/dev/tenancy" element={<Guarded><TenancyCheckPage /></Guarded>} />
        <Route path="/demo/extract" element={<Guarded><DemoExtractionPage /></Guarded>} />
        <Route path="/claims" element={<Guarded><ClaimsListPage /></Guarded>} />
        <Route path="/claims/:id" element={<Guarded><ClaimDetailPage /></Guarded>} />
        <Route path="/submit/new" element={<Guarded><NewClaimPage /></Guarded>} />
        <Route path="/approver" element={<Guarded><ApproverDashboardPage /></Guarded>} />
        <Route path="/approver/queue" element={<Guarded><ApprovalQueuePage /></Guarded>} />
        <Route path="/approver/auto-approved" element={<Guarded><AutoApprovedPage /></Guarded>} />
        <Route path="/approver/settled" element={<Guarded><SettledThisWeekPage /></Guarded>} />
        <Route path="/approver/external-verifications" element={<Guarded><ExternalVerificationQueuePage /></Guarded>} />
        <Route path="/approver/claims/:id" element={<Guarded><ClaimReviewPage /></Guarded>} />
        <Route path="/approver/settlement" element={<Guarded><SettlementQueuePage /></Guarded>} />
        <Route path="/approver/settlement/batches/:id" element={<Guarded><SettlementBatchPage /></Guarded>} />
        <Route path="/admin" element={<Guarded><AdminPlaceholderPage /></Guarded>} />
        <Route path="/admin/schemes" element={<Guarded><AdminSchemesPage /></Guarded>} />
        <Route path="/admin/schemes/new" element={<Guarded><SchemeFormPage /></Guarded>} />
        <Route path="/admin/schemes/upload" element={<Guarded><SchemeUploadPage /></Guarded>} />
        <Route path="/admin/schemes/:id/edit" element={<Guarded><SchemeFormPage /></Guarded>} />
      </Routes>
    </AppShell>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  );
}
